// src/context/AuthContext.js
import React, { createContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';

// Opruimen van de vroegere 'vertrouwd apparaat'-entry (2FA-bypass in de
// browser, verwijderd). Zonder deze regel blijft hij in localStorage staan.
try {
  localStorage.removeItem('Ix5VSiWU-elG6V&4^l#pCu{0w^$NZMCudDsiFijOdzSpatObSu');
} catch {
  // localStorage niet beschikbaar (privémodus e.d.)
}
import pdfDataCache from '../utils/pdfDataCache';

// Create context with default value
export const AuthContext = createContext({});

// Helper: Wacht een korte tijd (Kan eventueel weg als niet meer nodig)
// const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

// --- Hulpfuncties zonder React-state (buiten AuthProvider gehouden) ---

// Profiel (rol en themavoorkeur) van een gebruiker ophalen
function fetchProfile(userId) {
  return supabase.from('profiles').select('role, theme_preference').eq('id', userId).single();
}

// Bepaal of de huidige sessie volledig is aangemeld (2FA verplicht):
// geen geverifieerde factor -> needs_setup, aal1 -> needs_mfa, aal2 -> full.
async function determineAuthLevel() {
  try {
    const [aalResult, factorsResult] = await Promise.all([
      supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
      supabase.auth.mfa.listFactors(),
    ]);
    if (aalResult.error || factorsResult.error) {
      console.error(
        '[AuthContext] Kon 2FA-status niet bepalen:',
        aalResult.error || factorsResult.error,
      );
      return 'error';
    }
    const factors = [...(factorsResult.data?.totp || []), ...(factorsResult.data?.phone || [])];
    const hasVerifiedFactor = factors.some((factor) => factor.status === 'verified');
    if (!hasVerifiedFactor) return 'needs_setup';
    if (aalResult.data?.currentLevel === 'aal2') return 'full';
    return 'needs_mfa';
  } catch (error) {
    console.error('[AuthContext] Onverwachte fout bij bepalen 2FA-status:', error);
    return 'error';
  }
}

// Eerste sessie ophalen; bij een fout wordt null teruggegeven (en gelogd)
async function readInitialSession() {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      console.error('[AuthContext] Effect 1: supabase.auth.getSession() returned an error:', error); // Keep error logs
      return null;
    }
    return data?.session || null;
  } catch (getSessionError) {
    console.error(
      '[AuthContext] Effect 1: CRITICAL ERROR during supabase.auth.getSession() call:',
      getSessionError,
    ); // Keep critical error logs
    return null;
  }
}

// Inloggen; de onAuthStateChange-listener verwerkt de nieuwe state
async function loginWithPassword({ email, password }) {
  try {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('[AuthContext] Login error:', error); // Keep error logs
    return { error }; // Return error to the component
  }
}

// Lokale UI-state van de vorige gebruiker opruimen
function clearStoredUiState() {
  try {
    localStorage.removeItem('lastSelectedProjectId');
    sessionStorage.clear();
  } catch {
    // storage niet beschikbaar
  }
}

// AuthProvider component - must be a named function for Fast Refresh
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleLoading, setRoleLoading] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isSessionValid, setIsSessionValid] = useState(true);
  const [sessionExpiryTime, setSessionExpiryTime] = useState(null);
  const [, setShowSessionWarning] = useState(false);
  // Centrale 2FA-status, gedeeld door App (navbar), RequireAuth en Login:
  // 'checking' | 'none' | 'needs_setup' | 'needs_mfa' | 'full' | 'error'
  const [authLevel, setAuthLevel] = useState('checking');
  const authLevelRequestRef = useRef(0);
  const { setMode } = useTheme();

  // --- Refs --- (Simplified, mainly for tracking)
  const initStartedRef = useRef(false);
  const authListenerSubscriptionRef = useRef(null);

  // --- Callbacks --- (Simplified where possible)

  // Function to fetch user role - stays async
  const fetchUserRole = useCallback(
    async (userId) => {
      if (!userId) return 'user';
      setRoleLoading(true); // Start role loading
      try {
        // console.log('[AuthContext] Fetching role for user:', userId);
        const { data, error } = await fetchProfile(userId);
        if (error) {
          console.error('[AuthContext] Error fetching role:', error); // Keep error logs
          setRole('user');
          setRoleLoading(false); // Finish role loading even on error
          return 'user';
        }
        const fetchedRole = data?.role || 'user';
        // Set theme preference if it exists
        if (data?.theme_preference) {
          setMode(data.theme_preference);
        }
        // console.log('[AuthContext] Fetched role:', fetchedRole);
        setRole(fetchedRole);
        // console.log(`[AuthContext] Role set to: ${fetchedRole} for user: ${userId}`);
        setRoleLoading(false);
        return fetchedRole;
      } catch (error) {
        console.error('[AuthContext] Unexpected error in fetchUserRole:', error); // Keep error logs
        setRole('user');
        setRoleLoading(false); // Finish role loading even on error
        return 'user';
      }
    },
    [setMode],
  );

  // Function to handle session data and update state SYNCHRONOUSLY
  const handleSessionData = useCallback(
    (session) => {
      const currentUser = session?.user || null;
      // Bewaar dezelfde object-referentie als de user ID niet veranderd is.
      // Dit voorkomt cascaderende re-renders door de hele app bij elke token refresh.
      setUser((prev) => {
        if (prev?.id && prev.id === currentUser?.id) return prev;
        return currentUser;
      });
      setSessionExpiryTime(
        session?.expires_at ? new Date(session.expires_at * 1000).getTime() : null,
      );
      setIsSessionValid(!!currentUser);
      setShowSessionWarning(false); // Reset warning on session update

      // Mark initial session check as done and stop loading
      if (!sessionChecked) {
        setSessionChecked(true);
        setLoading(false);
      }
    },
    [sessionChecked],
  ); // Depends on sessionChecked to correctly set it once

  // 2FA-status opnieuw bepalen (zie determineAuthLevel)
  const refreshAuthLevel = useCallback(async () => {
    const requestId = ++authLevelRequestRef.current;
    const nextLevel = await determineAuthLevel();
    // Alleen het antwoord op de laatste aanvraag telt
    if (requestId === authLevelRequestRef.current) setAuthLevel(nextLevel);
    return nextLevel;
  }, []);

  // --- Effects ---

  // Effect 1: Run ONLY ONCE on mount for initial session check
  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    let mounted = true;

    // console.log('[AuthContext] Effect 1: Running initial getSession setup...');

    const initSession = async () => {
      try {
        // console.log('[AuthContext] Effect 1: Waiting 50ms before getSession...');
        // await sleep(50); // Remove sleep if not needed

        if (!mounted) return;

        // console.log('[AuthContext] Effect 1: Attempting supabase.auth.getSession()...');
        const session = await readInitialSession();

        if (mounted) {
          handleSessionData(session);
        }
      } catch (outerError) {
        console.error(
          '[AuthContext] Effect 1: Error in outer initSession catch block:',
          outerError,
        ); // Keep error logs
        if (mounted) {
          // console.log('[AuthContext] Effect 1: Calling handleSessionData(null) due to outer catch.');
          handleSessionData(null);
        }
      }
    };

    initSession();

    return () => {
      // console.log("[AuthContext] Effect 1: Cleanup (mount related)");
      mounted = false;
    };
  }, [handleSessionData]);

  // Effect 2: Setup onAuthStateChange listener AFTER initial check is done
  useEffect(() => {
    if (!sessionChecked) {
      // console.log("[AuthContext] Effect 2: Skipping auth listener setup (session not checked yet).");
      return; // Don't setup listener until initial check is done
    }

    // console.log('[AuthContext] Effect 2: Setting up onAuthStateChange listener...');
    let mounted = true;

    const authListenerCallback = (event, session) => {
      // console.log(`[AuthContext] Effect 2: onAuthStateChange event: ${event}`);
      if (mounted) {
        handleSessionData(session); // Handle updates from the listener
        if (event === 'SIGNED_OUT') {
          authLevelRequestRef.current += 1;
          setAuthLevel('none');
          setMode('light'); // Reset theme to light on sign out
        } else if (
          event === 'MFA_CHALLENGE_VERIFIED' ||
          event === 'SIGNED_IN' ||
          event === 'USER_UPDATED'
        ) {
          // Niet awaiten binnen de callback (supabase-js kan anders vastlopen)
          setTimeout(() => {
            if (mounted) refreshAuthLevel();
          }, 0);
        }
      }
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(authListenerCallback);
    authListenerSubscriptionRef.current = subscription; // Store for cleanup

    // console.log('[AuthContext] Effect 2: Auth listener is active.');

    return () => {
      // console.log('[AuthContext] Effect 2: Cleaning up auth listener...');
      mounted = false;
      authListenerSubscriptionRef.current?.unsubscribe();
      authListenerSubscriptionRef.current = null;
    };
  }, [sessionChecked, handleSessionData, refreshAuthLevel, setMode]); // Run when sessionChecked becomes true

  // Effect 2b: 2FA-status opnieuw bepalen bij een andere gebruiker
  useEffect(() => {
    if (!sessionChecked) return;
    if (!user?.id) {
      authLevelRequestRef.current += 1;
      setAuthLevel('none');
      return;
    }
    setAuthLevel('checking');
    refreshAuthLevel();
  }, [user?.id, sessionChecked, refreshAuthLevel]);

  // Effect 3: Fetch user role when user object changes
  useEffect(() => {
    if (user?.id) {
      // console.log('[AuthContext] Effect 3: User changed, fetching role...');
      fetchUserRole(user.id);
    } else {
      // console.log('[AuthContext] Effect 3: No user, setting role to null.');
      setRole(null); // Clear role if no user
      setRoleLoading(false); // Ensure role loading is false if no user
    }
  }, [user, fetchUserRole]); // Depend on user object and fetchUserRole callback

  // --- Login/Logout Functions --- (Simplified, rely on listener for state updates)
  // loginWithPassword staat buiten het component en is dus al stabiel
  const login = loginWithPassword;

  const logout = useCallback(async () => {
    try {
      // Clear cache on logout to free memory
      pdfDataCache.clear();

      // Reset theme to light before signing out
      setMode('light');

      // Lokale UI-state van de vorige gebruiker opruimen
      clearStoredUiState();

      // Sign out, the onAuthStateChange listener will handle the state update
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Logout error:', error);
      return { error };
    }
  }, [setMode]);

  // --- Context Value --- (Removed SessionManager related values)
  const contextValue = useMemo(
    () => ({
      user,
      role,
      loading,
      roleLoading,
      sessionChecked,
      isSessionValid,
      sessionExpiryTime,
      authLevel,
      isFullyAuthenticated: !!user && authLevel === 'full',
      refreshAuthLevel,
      login,
      logout,
    }),
    [
      user,
      role,
      loading,
      roleLoading,
      sessionChecked,
      isSessionValid,
      sessionExpiryTime,
      authLevel,
      refreshAuthLevel,
      login,
      logout,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {/* Removed Session Warning Popup */}
    </AuthContext.Provider>
  );
}

// Export AuthProvider as named export for Fast Refresh compatibility
export { AuthProvider };
